import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { Strategy, Trader } from "../api/types";
import { useToast, errText, JsonArea, DirtyChip, EnabledChip } from "../components/ui";

export default function Strategies({ onChanged }: { onChanged: () => void }) {
  const notify = useToast();
  const [rows, setRows] = useState<Strategy[]>([]);
  const [traders, setTraders] = useState<Trader[]>([]);
  const [nn, setNn] = useState("");

  const load = () => {
    api.listStrategies().then(setRows).catch((e) => notify({ kind: "error", msg: errText(e) }));
    api.listTraders().then(setTraders).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addNew = async () => {
    if (!nn.trim()) return;
    try {
      await api.upsertStrategy(nn.trim(), {
        class_path: "strategies.double_ma.DoubleMaStrategy",
        symbol: "BTC-USDT", trader_ref: null, group_name: null, params: {}, enabled: true,
      });
      setNn(""); load(); onChanged();
    } catch (e) { notify({ kind: "error", msg: errText(e) }); }
  };

  return (
    <div>
      <div className="panel row">
        <div><label>新增策略名称</label><input value={nn} onChange={(e) => setNn(e.target.value)} placeholder="如 btc_momentum_1m" /></div>
        <div style={{ flex: "0 0 auto" }}><button className="primary" onClick={addNew}>新增</button></div>
      </div>
      {rows.map((s) => (
        <StrategyCard key={s.name} s={s} traders={traders.map((t) => t.name)} onSaved={() => { load(); onChanged(); }} />
      ))}
      {rows.length === 0 && <div className="muted">暂无策略</div>}
    </div>
  );
}

function StrategyCard({ s, traders, onSaved }: { s: Strategy; traders: string[]; onSaved: () => void }) {
  const notify = useToast();
  const [enabled, setEnabled] = useState(s.enabled);
  const [classPath, setClassPath] = useState(s.class_path);
  const [symbol, setSymbol] = useState(s.symbol);
  const [traderRef, setTraderRef] = useState(s.trader_ref || "");
  const [group, setGroup] = useState(s.group_name || "");
  const [params, setParams] = useState<Record<string, unknown>>(s.params || {});

  const save = async () => {
    try {
      await api.upsertStrategy(s.name, {
        class_path: classPath, symbol,
        trader_ref: traderRef || null, group_name: group || null,
        params, enabled,
      });
      onSaved();
      notify({ kind: "ok", msg: `已保存草稿: ${s.name}` });
    } catch (e) { notify({ kind: "error", msg: errText(e) }); }
  };
  const remove = async () => {
    if (!confirm(`删除策略 ${s.name}?`)) return;
    try { await api.deleteStrategy(s.name); onSaved(); } catch (e) { notify({ kind: "error", msg: errText(e) }); }
  };
  const hotUpdate = async () => {
    try {
      const r = await api.hotUpdateParams(s.name, params);
      notify({ kind: "ok", msg: `已热更新运行中参数: ${Object.keys(r.applied).join(", ") || "无变化"}` });
    } catch (e) { notify({ kind: "error", msg: errText(e) }); }
  };

  return (
    <div className="card">
      <div className="card-head">
        <span className="title mono">{s.name}</span>
        <EnabledChip enabled={enabled} />
        <DirtyChip dirty={s.dirty} />
        <span className="spacer" />
        <button onClick={() => setEnabled(!enabled)}>{enabled ? "停用" : "启用"}</button>
        <button onClick={save}>保存草稿</button>
        <button className="danger" onClick={remove}>删除</button>
      </div>
      <div className="grid2" style={{ marginTop: 10 }}>
        <div><label>class_path</label><input className="mono" value={classPath} onChange={(e) => setClassPath(e.target.value)} /></div>
        <div><label>symbol</label><input value={symbol} onChange={(e) => setSymbol(e.target.value)} /></div>
        <div><label>trader_ref</label>
          <select value={traderRef} onChange={(e) => setTraderRef(e.target.value)}>
            <option value="">（无）</option>
            {traders.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div><label>group_name（策略组，可空）</label><input value={group} onChange={(e) => setGroup(e.target.value)} /></div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label>参数 params（JSON）</label>
        <JsonArea value={params} onChange={setParams} rows={5} />
        <div style={{ marginTop: 6 }}>
          <button onClick={hotUpdate} title="不经 apply，直接热更新运行中策略的白名单参数">即时热更参数</button>
          <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>结构性参数（bar_interval）不可热更，需空仓后经 apply 重建</span>
        </div>
      </div>
    </div>
  );
}
